import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A hash router in forty lines.
 *
 * The console is a single internal operator tool served as static files; adding a routing
 * library to it would be more dependency than navigation. Hash routes also mean the whole
 * thing works from any path a reverse proxy puts it behind, with no server rewrite rules.
 */

export interface Route {
  path: string;
  params: Record<string, string>;
}

function parseHash(hash: string): Route {
  const raw = hash.replace(/^#/, '') || '/';
  const [path = '/', query = ''] = raw.split('?');
  return { path, params: Object.fromEntries(new URLSearchParams(query)) };
}

const RouterContext = createContext<{ route: Route; navigate: (to: string) => void }>({
  route: { path: '/', params: {} },
  navigate: () => undefined,
});

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback((to: string) => {
    window.location.hash = to;
  }, []);

  const value = useMemo(() => ({ route, navigate }), [route, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRoute(): Route {
  return useContext(RouterContext).route;
}

export function useNavigate(): (to: string) => void {
  return useContext(RouterContext).navigate;
}

/** Matches `/runs/:runId` against `/runs/abc`, returning the captured segments. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const captured: Record<string, string> = {};
  for (const [index, part] of patternParts.entries()) {
    const actual = pathParts[index]!;
    if (part.startsWith(':')) captured[part.slice(1)] = decodeURIComponent(actual);
    else if (part !== actual) return null;
  }
  return captured;
}

/** Giriş formunun kendi yolu. Kök adres artık karşılama sayfası. */
export const LOGIN_PATH = '/giris';

export type Gate = { kind: 'landing' } | { kind: 'login' } | { kind: 'app'; path: string };

/**
 * Bir yolun oturum durumuna göre neye karşılık geldiği.
 *
 * Saf bir fonksiyon, çünkü buradaki üç kuralın da sessizce bozulma huyu var:
 *
 * - Oturum yokken KÖK karşılama sayfası, başka her yol giriş formu. Ayrım kasten: süresi
 *   dolmuş bir oturumla `#/proxies` yer imine dönen kişi karşılama sayfasında takılmamalı.
 *
 * - Oturum VARKEN `/giris` kök sayılıyor. Giriş başarılı olduğunda adres formda kalıyor
 *   ve o yolun karşılığı olan bir sayfa yok — kullanıcı giriş yaptığı hâlde "böyle bir
 *   sayfa yok" görüyordu.
 */
export function gate(path: string, hasSession: boolean): Gate {
  if (!hasSession) return path === '/' ? { kind: 'landing' } : { kind: 'login' };
  return { kind: 'app', path: path === LOGIN_PATH ? '/' : path };
}
